import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Wallet, TrendingUp, DollarSign, Settings, AlertCircle, CheckCircle2, Loader2, Landmark, ExternalLink } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { payoutAPI } from '../services/api';

const DriverPayouts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [activeTab, setActiveTab] = useState('balance');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    payout_preference: 'manual',
    bank_account: '',
    sort_code: '',
    account_name: ''
  });

  useEffect(() => {
    fetchData();

    const params = new URLSearchParams(window.location.search);
    if (params.get('onboarding') === 'complete') {
      toast({ title: 'Stripe onboarding submitted', description: 'Checking your account status...' });
      window.history.replaceState({}, '', '/driver/payouts');
    } else if (params.get('onboarding') === 'refresh') {
      window.history.replaceState({}, '', '/driver/payouts');
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balanceData, prefsData, connectData] = await Promise.all([
        payoutAPI.getBalance(),
        payoutAPI.getPreferences(),
        payoutAPI.getConnectStatus()
      ]);
      setBalance(balanceData);
      setPreferences(prefsData);
      setConnectStatus(connectData);
      setPayoutForm({
        payout_preference: prefsData.payout_preference || 'manual',
        bank_account: prefsData.bank_account || '',
        sort_code: prefsData.sort_code || '',
        account_name: prefsData.account_name || ''
      });
    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load payout information',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    setConnecting(true);
    try {
      const response = await payoutAPI.startConnectOnboarding();
      window.location.href = response.onboarding_url;
    } catch (error) {
      toast({
        title: 'Could not start onboarding',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
      setConnecting(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    
    if (!amount || amount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid withdrawal amount',
        variant: 'destructive'
      });
      return;
    }

    if (amount < 10) {
      toast({
        title: 'Minimum Withdrawal',
        description: 'Minimum withdrawal amount is £10.00',
        variant: 'destructive'
      });
      return;
    }

    setWithdrawing(true);
    try {
      const response = await payoutAPI.requestWithdrawal(amount);
      toast({
        title: 'Success!',
        description: response.message,
        duration: 5000
      });
      setWithdrawAmount('');
      fetchData(); // Refresh balance
    } catch (error) {
      toast({
        title: 'Withdrawal Failed',
        description: error.response?.data?.detail || 'Failed to process withdrawal',
        variant: 'destructive'
      });
    } finally {
      setWithdrawing(false);
    }
  };

  const handleSavePreferences = async () => {
    setSavingPrefs(true);
    try {
      const response = await payoutAPI.updatePreferences(payoutForm);
      toast({
        title: 'Success!',
        description: response.message,
        duration: 5000
      });
      fetchData(); // Refresh preferences
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update preferences',
        variant: 'destructive'
      });
    } finally {
      setSavingPrefs(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, #0F1D2B, #17283A)' }}>
        <Loader2 className="w-8 h-8 text-wheely-orange-light animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #0F1D2B, #17283A)' }}>
      {/* Header */}
      <div className="border-b" style={{ background: 'linear-gradient(to right, #1E3448, #0F1D2B)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/driver/dashboard')}
              className="text-wheely-orange-light hover:text-wheely-orange-light"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-white">Payouts & Earnings</h1>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Balance Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card style={{ background: 'linear-gradient(to bottom right, #E17431, #EB9868)', borderColor: 'rgba(235, 152, 104, 0.3)' }}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Pending
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-white">£{balance?.balance_pending?.toFixed(2) || '0.00'}</p>
              <p className="text-orange-100 text-sm mt-1">Jobs in progress</p>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(to bottom right, #10b981, #059669)', borderColor: 'rgba(16, 185, 129, 0.3)' }}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-white">£{balance?.balance_available?.toFixed(2) || '0.00'}</p>
              <p className="text-green-100 text-sm mt-1">Ready to withdraw</p>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(to bottom right, #E17431, #2EB2D5)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Total Earned
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-white">£{balance?.total_earned?.toFixed(2) || '0.00'}</p>
              <p className="text-cyan-100 text-sm mt-1">All time earnings</p>
            </CardContent>
          </Card>
        </div>

        {/* Commission Info */}
        <Card className="mb-8" style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Your Commission Rate</p>
                <p className="text-white font-semibold text-lg">
                  {balance?.commission_rate}% ({balance?.membership_tier} tier)
                </p>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-sm">Total Commission Paid</p>
                <p className="text-white font-semibold text-lg">
                  £{balance?.total_commission?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('balance')}
            style={{
              background: activeTab === 'balance' ? 'linear-gradient(to right, #E17431, #EB9868)' : 'rgba(30, 58, 74, 0.8)',
              color: activeTab === 'balance' ? 'white' : '#94a3b8'
            }}
          >
            Withdraw
          </Button>
          <Button
            onClick={() => setActiveTab('settings')}
            style={{
              background: activeTab === 'settings' ? 'linear-gradient(to right, #E17431, #EB9868)' : 'rgba(30, 58, 74, 0.8)',
              color: activeTab === 'settings' ? 'white' : '#94a3b8'
            }}
          >
            <Settings className="w-4 h-4 mr-2" />
            Payout Settings
          </Button>
        </div>

        {/* Withdraw Tab */}
        {activeTab === 'balance' && (
          <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
            <CardHeader>
              <CardTitle className="text-white">Request Withdrawal</CardTitle>
              <CardDescription className="text-gray-300">
                {preferences?.payout_preference === 'manual' ? (
                  'Withdraw your available balance to your bank account'
                ) : (
                  <div className="flex items-start gap-2 mt-2">
                    <AlertCircle className="w-4 h-4 text-wheely-orange-light mt-0.5" />
                    <span className="text-wheely-orange-light">
                      Automatic weekly payouts are enabled. Next payout on Monday.
                    </span>
                  </div>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {preferences?.payout_preference === 'manual' ? (
                <>
                  {!connectStatus?.payouts_enabled ? (
                    <div className="bg-wheely-orange/10 border border-wheely-orange/30 rounded-lg p-4 flex gap-3">
                      <AlertCircle className="w-5 h-5 text-wheely-orange flex-shrink-0" />
                      <div>
                        <p className="text-orange-300 font-semibold">Connect your bank account</p>
                        <p className="text-sm text-gray-300 mt-1">
                          Set up payouts with Stripe in Payout Settings before requesting a withdrawal.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="amount" className="text-white">Withdrawal Amount (£)</Label>
                        <Input
                          id="amount"
                          type="number"
                          step="0.01"
                          min="10"
                          max={balance?.balance_available || 0}
                          placeholder="Enter amount (min £10)"
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          style={{ background: 'rgba(15, 23, 42, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}
                          className="text-white"
                        />
                        <p className="text-sm text-gray-400">
                          Available: £{balance?.balance_available?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <Button
                        onClick={handleWithdraw}
                        disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) > (balance?.balance_available || 0)}
                        className="w-full"
                        style={{ background: 'linear-gradient(to right, #10b981, #059669)' }}
                      >
                        {withdrawing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Request Withdrawal
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <div className="bg-wheely-orange/20/10 border border-wheely-orange/30/30 rounded-lg p-4">
                  <p className="text-wheely-orange-light">
                    Automatic payouts are processed every Monday. Your available balance will be transferred automatically.
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Switch to manual payouts in settings to request withdrawals on-demand.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
            <CardHeader>
              <CardTitle className="text-white">Payout Preferences</CardTitle>
              <CardDescription className="text-gray-300">
                Choose how you want to receive your earnings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Payout Method */}
              <div className="space-y-3">
                <Label className="text-white text-lg">Payout Method</Label>
                <div className="space-y-3">
                  <div
                    onClick={() => setPayoutForm({...payoutForm, payout_preference: 'manual'})}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      payoutForm.payout_preference === 'manual'
                        ? 'border-wheely-orange bg-wheely-orange/10'
                        : 'border-wheely-navy-700 bg-wheely-navy-800/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center ${
                        payoutForm.payout_preference === 'manual' ? 'border-wheely-orange' : 'border-wheely-navy-700'
                      }`}>
                        {payoutForm.payout_preference === 'manual' && (
                          <div className="w-3 h-3 rounded-full bg-wheely-orange"></div>
                        )}
                      </div>
                      <div>
                        <p className="text-white font-semibold">Manual Withdrawals</p>
                        <p className="text-sm text-gray-400">Request payouts whenever you want (minimum £10)</p>
                      </div>
                    </div>
                  </div>

                  <div
                    onClick={() => setPayoutForm({...payoutForm, payout_preference: 'auto_weekly'})}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      payoutForm.payout_preference === 'auto_weekly'
                        ? 'border-wheely-orange/30 bg-wheely-orange/20/10'
                        : 'border-wheely-navy-700 bg-wheely-navy-800/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center ${
                        payoutForm.payout_preference === 'auto_weekly' ? 'border-wheely-orange/30' : 'border-wheely-navy-700'
                      }`}>
                        {payoutForm.payout_preference === 'auto_weekly' && (
                          <div className="w-3 h-3 rounded-full bg-wheely-orange/20"></div>
                        )}
                      </div>
                      <div>
                        <p className="text-white font-semibold">Automatic Weekly Payouts</p>
                        <p className="text-sm text-gray-400">Automatic transfers every Monday to your bank account</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSavePreferences}
                disabled={savingPrefs}
                className="w-full"
                style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}
              >
                {savingPrefs ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Save Preferences
                  </>
                )}
              </Button>

              {/* Stripe Connect bank account */}
              <div className="space-y-3 pt-4 border-t border-wheely-navy-700">
                <Label className="text-white text-lg">Bank Account</Label>
                {connectStatus?.payouts_enabled ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <div>
                      <p className="text-green-300 font-semibold">Connected with Stripe</p>
                      <p className="text-sm text-gray-300 mt-1">Withdrawals go straight to your bank account.</p>
                    </div>
                  </div>
                ) : connectStatus?.details_submitted ? (
                  <div className="bg-wheely-orange/10 border border-wheely-orange/30 rounded-lg p-4">
                    <p className="text-orange-300 font-semibold">Verification in progress</p>
                    <p className="text-sm text-gray-300 mt-1">
                      Stripe is reviewing your details. This usually only takes a few minutes.
                    </p>
                  </div>
                ) : (
                  <div className="bg-wheely-navy-800/30 border border-wheely-navy-700 rounded-lg p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <Landmark className="w-5 h-5 text-wheely-orange-light mt-0.5" />
                      <p className="text-sm text-gray-300">
                        Withdrawals are paid out securely through Stripe. Connect your bank
                        account to enable withdrawals - Wheely never sees or stores your bank details.
                      </p>
                    </div>
                    <Button
                      onClick={handleConnectStripe}
                      disabled={connecting}
                      className="w-full"
                      style={{ background: 'linear-gradient(to right, #635bff, #837ef0)' }}
                    >
                      {connecting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Redirecting to Stripe...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Connect bank account with Stripe
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DriverPayouts;
